import { useAssets } from 'hooks/games/useAssets'
import { useAssetsWithVariation } from 'hooks/useAssetsVariation'
import { isEmpty, isEqual } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { LVG_ASSETS } from 'constants/games/lvg/lvgAssets'

const filterAssetWithVariation = (AllAssetWithVariation: TExtendedAsset[], currentGameAssets: TBaseAsset[]) =>
  AllAssetWithVariation.filter((asset1) => currentGameAssets.some((asset2) => asset2.id === asset1.id))

export const useAssetsForDropdown = (): {
  assets: TExtendedAsset[]
  hotAssets: TExtendedAsset[]
  coldAssets: TExtendedAsset[]
  featuredAssets: TExtendedAsset[]
  assetsTypes: string[]
  setFilter: (assetId: string) => void
  getAssetWithVariation: (asset: TExtendedAsset) => TExtendedAsset
} => {
  const { currentGameAssets } = useAssets()
  const { assetWithVariation: AllAssetWithVariation } = useAssetsWithVariation(LVG_ASSETS)
  const assetWithVariation = filterAssetWithVariation(AllAssetWithVariation, currentGameAssets)
  const [assets, setAssets] = useState<TExtendedAsset[]>(assetWithVariation)
  const [filter, setFilter] = useState<string>('')

  useEffect(() => {
    if (isEqual(assetWithVariation, assets) === false) setAssets(assetWithVariation)
  }, [assetWithVariation])

  const filteredAssets: (TExtendedAsset & { position: number })[] = useMemo(
    () =>
      assets
        .map((asset, idx) => ({ ...asset, position: idx }))
        .filter((asset) => {
          if (asset.hideInUi) return false
          if (filter === '') return true
          return (
            asset.id.toLowerCase().includes(filter.toLowerCase()) ||
            asset.uiName.toLowerCase().includes(filter.toLowerCase())
          )
        }),
    [assets, filter]
  )

  const assetsSpliter = useMemo((): { hot: TExtendedAsset[]; cold: TExtendedAsset[]; featured: TExtendedAsset[] } => {
    let hot: TExtendedAsset[] = []
    let cold: TExtendedAsset[] = []
    let featured: TExtendedAsset[] = []

    filteredAssets.forEach((asset) => {
      if (asset.featured) featured.push(asset)
      else if (asset.position <= 4) hot.push(asset)
      else cold.push(asset)
    })
    return { hot, cold, featured }
  }, [filteredAssets])

  const assetsTypes = useMemo(() => {
    if (isEmpty(filteredAssets)) return ['No assets']
    const assetsTypes = filteredAssets.map((asset) => asset.type).filter((type, idx, arr) => arr.indexOf(type) === idx)
    //  if (assetsTypes.length === 1) return []
    return assetsTypes
  }, [filteredAssets])

  const getAssetWithVariation = (asset: TExtendedAsset) => {
    const assetWithVariation = AllAssetWithVariation.find((a) => a.id === asset.id)
    if (assetWithVariation) return assetWithVariation
    return asset
  }

  return {
    assets: filteredAssets,
    hotAssets: assetsSpliter.hot,
    coldAssets: assetsSpliter.cold,
    featuredAssets: assetsSpliter.featured,
    assetsTypes,
    setFilter,
    getAssetWithVariation,
  }
}
